'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  clamp,
  drawFramedImageToCanvas,
  encodeAnimatedPng,
  formatFileSize,
  getAnimationDuration,
  getBaseName,
  getCanvasRgba,
  isPngFile,
  loadPngImage,
  sanitizeHexColor,
  type CanvasFramingMode,
} from '@/lib/browser-png-tools';

type BackgroundMode = 'transparent' | 'white' | 'black' | 'custom';
type LoopMode = 'infinite' | 'once' | 'custom';

type FrameItem = {
  id: string;
  file: File;
  name: string;
  size: number;
  width: number;
  height: number;
  delayMs: number;
  previewUrl: string;
  element: HTMLImageElement;
};

function createFrameId(file: File, index: number) {
  return `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function BrowserAnimatedPngMaker() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const framesRef = useRef<FrameItem[]>([]);
  const generatedPreviewUrlRef = useRef<string | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [frames, setFrames] = useState<FrameItem[]>([]);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [framingMode, setFramingMode] = useState<CanvasFramingMode>('fit');
  const [defaultDelayMs, setDefaultDelayMs] = useState(120);
  const [loopMode, setLoopMode] = useState<LoopMode>('infinite');
  const [customLoopCount, setCustomLoopCount] = useState(3);
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>('transparent');
  const [customHex, setCustomHex] = useState('#ffffff');
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultDuration, setResultDuration] = useState<number | null>(null);

  const exportBackground = useMemo(() => {
    switch (backgroundMode) {
      case 'white':
        return '#ffffff';
      case 'black':
        return '#000000';
      case 'custom':
        return sanitizeHexColor(customHex);
      case 'transparent':
      default:
        return null;
    }
  }, [backgroundMode, customHex]);

  const canvasSize = useMemo(() => {
    if (!frames.length) {
      return null;
    }

    return frames.reduce(
      (accumulator, frame) => ({
        width: Math.max(accumulator.width, frame.width),
        height: Math.max(accumulator.height, frame.height),
      }),
      { width: 1, height: 1 },
    );
  }, [frames]);

  const sourceBytes = useMemo(() => frames.reduce((sum, frame) => sum + frame.size, 0), [frames]);
  const sourceDuration = useMemo(
    () => getAnimationDuration(frames.map((frame) => clamp(Math.round(frame.delayMs), 20, 5000))),
    [frames],
  );

  useEffect(() => {
    framesRef.current = frames;
  }, [frames]);

  useEffect(() => {
    generatedPreviewUrlRef.current = generatedPreviewUrl;
  }, [generatedPreviewUrl]);

  useEffect(() => {
    downloadUrlRef.current = downloadUrl;
  }, [downloadUrl]);

  useEffect(() => {
    return () => {
      framesRef.current.forEach((frame) => URL.revokeObjectURL(frame.previewUrl));
      [generatedPreviewUrlRef.current, downloadUrlRef.current].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  function clearOutputState() {
    [generatedPreviewUrl, downloadUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setGeneratedPreviewUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
    setResultDuration(null);
  }

  function replaceFrames(nextFrames: FrameItem[]) {
    clearOutputState();
    setFrames(nextFrames);
  }

  async function handleFileSelection(fileList: FileList | File[] | null) {
    setErrorMessage(null);
    setCreditMessage(null);

    const selectedFiles = fileList ? Array.from(fileList) : [];

    if (!selectedFiles.length) {
      return;
    }

    const pngFiles = selectedFiles.filter(isPngFile);

    if (!pngFiles.length) {
      setErrorMessage('Choose PNG files only. This maker builds APNG files from PNG frames.');
      return;
    }

    try {
      const nextFrames = await Promise.all(
        pngFiles.map(async (file, index) => {
          const loaded = await loadPngImage(file);

          return {
            id: createFrameId(file, index),
            file,
            name: file.name,
            size: file.size,
            width: loaded.width,
            height: loaded.height,
            delayMs: defaultDelayMs,
            previewUrl: URL.createObjectURL(file),
            element: loaded.element,
          } satisfies FrameItem;
        }),
      );

      clearOutputState();
      setFrames((currentFrames) => [...currentFrames, ...nextFrames]);

      if (pngFiles.length !== selectedFiles.length) {
        setErrorMessage('Only PNG files were added. Non-PNG files were skipped.');
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PNG frames could not be added.',
      );
    }
  }

  function removeFrame(frameId: string) {
    setErrorMessage(null);
    setCreditMessage(null);
    clearOutputState();
    setFrames((currentFrames) => {
      const frameToRemove = currentFrames.find((frame) => frame.id === frameId);

      if (frameToRemove) {
        URL.revokeObjectURL(frameToRemove.previewUrl);
      }

      return currentFrames.filter((frame) => frame.id !== frameId);
    });
  }

  function moveFrame(frameId: string, direction: -1 | 1) {
    setErrorMessage(null);
    setCreditMessage(null);
    clearOutputState();
    setFrames((currentFrames) => {
      const currentIndex = currentFrames.findIndex((frame) => frame.id === frameId);
      const nextIndex = currentIndex + direction;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= currentFrames.length) {
        return currentFrames;
      }

      const reordered = [...currentFrames];
      const [frame] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, frame);
      return reordered;
    });
  }

  function updateFrameDelay(frameId: string, value: number) {
    setErrorMessage(null);
    setCreditMessage(null);
    clearOutputState();
    setFrames((currentFrames) =>
      currentFrames.map((frame) =>
        frame.id === frameId ? { ...frame, delayMs: clamp(value || 0, 20, 5000) } : frame,
      ),
    );
  }

  function applyDefaultDelayToAllFrames() {
    if (!frames.length) {
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);
    replaceFrames(
      frames.map((frame) => ({
        ...frame,
        delayMs: clamp(defaultDelayMs, 20, 5000),
      })),
    );
  }

  async function runGeneration() {
    if (frames.length < 2 || !canvasSize) {
      setErrorMessage('Add at least 2 PNG frames to build an animated PNG.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'animated-png-maker',
        'Credits are not available for another animated PNG export right now.',
      );
      const rgbaFrames = frames.map((frame) => {
        const canvas = drawFramedImageToCanvas(
          frame.element,
          canvasSize.width,
          canvasSize.height,
          framingMode,
          exportBackground,
        );

        return new Uint8Array(getCanvasRgba(canvas).data);
      });
      const delays = frames.map((frame) => clamp(Math.round(frame.delayMs), 20, 5000));
      const loopCount =
        loopMode === 'infinite' ? 0 : loopMode === 'once' ? 1 : clamp(customLoopCount, 1, 999);
      const encoded = encodeAnimatedPng({
        frames: rgbaFrames,
        width: canvasSize.width,
        height: canvasSize.height,
        colorCount: 0,
        delays,
        loopCount,
      });
      const blob = new Blob([encoded], { type: 'image/png' });
      const previewUrl = URL.createObjectURL(blob);
      const nextDownloadUrl = URL.createObjectURL(blob);

      setGeneratedPreviewUrl(previewUrl);
      setDownloadUrl(nextDownloadUrl);
      setDownloadName(`${getBaseName(frames[0].name)}-animated.png`);
      setResultSize(blob.size);
      setResultDuration(getAnimationDuration(delays));
      setCreditMessage(formatToolCreditSuccess('Animated PNG export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The animated PNG could not be generated.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Build an animated PNG from multiple PNG frames</h2>
          <p>
            Add a sequence of PNG files, control frame timing, and export one APNG locally in the
            browser without switching to GIF or video.
          </p>
        </div>
        <span className="tool-live-badge">APNG</span>
      </div>

      <div className="tool-live-routes" aria-label="Canvas framing">
        <button
          className={`tool-live-route ${framingMode === 'fit' ? 'tool-live-route-active' : ''}`}
          onClick={() => {
            setFramingMode('fit');
            clearOutputState();
          }}
          type="button"
        >
          Fit frames
        </button>
        <button
          className={`tool-live-route ${framingMode === 'fill' ? 'tool-live-route-active' : ''}`}
          onClick={() => {
            setFramingMode('fill');
            clearOutputState();
          }}
          type="button"
        >
          Fill canvas
        </button>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {frames.length
                  ? `Add more PNG frames or drop them here`
                  : 'Choose PNG frames or drop them here'}
              </strong>
              <span>
                {frames.length
                  ? `${frames.length} frames loaded • ${formatFileSize(sourceBytes)} total source size`
                  : 'Upload 2 or more PNG files in order. Great for stickers, UI loops, product spins, and motion graphics.'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            multiple
            accept="image/png"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>Default frame delay</span>
              <input
                min="20"
                onChange={(event) => {
                  setDefaultDelayMs(clamp(Number(event.target.value) || 20, 20, 5000));
                  clearOutputState();
                }}
                type="number"
                value={defaultDelayMs}
              />
            </label>
            <label className="field">
              <span>Loop mode</span>
              <select
                onChange={(event) => {
                  setLoopMode(event.target.value as LoopMode);
                  clearOutputState();
                }}
                value={loopMode}
              >
                <option value="infinite">Infinite</option>
                <option value="once">Play once</option>
                <option value="custom">Custom count</option>
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="field">
              <span>Background</span>
              <select
                onChange={(event) => {
                  setBackgroundMode(event.target.value as BackgroundMode);
                  clearOutputState();
                }}
                value={backgroundMode}
              >
                <option value="transparent">Transparent</option>
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="custom">Custom hex</option>
              </select>
            </label>
            <label className="field">
              <span>Apply delay</span>
              <button
                className="button button-ghost"
                disabled={!frames.length}
                onClick={applyDefaultDelayToAllFrames}
                type="button"
              >
                Apply to all frames
              </button>
            </label>
          </div>

          {backgroundMode === 'custom' || loopMode === 'custom' ? (
            <div className="tool-live-controls">
              {backgroundMode === 'custom' ? (
                <label className="field">
                  <span>Custom color</span>
                  <input
                    onChange={(event) => {
                      setCustomHex(event.target.value);
                      clearOutputState();
                    }}
                    placeholder="#ffffff"
                    type="text"
                    value={customHex}
                  />
                </label>
              ) : (
                <div />
              )}
              {loopMode === 'custom' ? (
                <label className="field">
                  <span>Loop count</span>
                  <input
                    min="1"
                    onChange={(event) => {
                      setCustomLoopCount(clamp(Number(event.target.value) || 1, 1, 999));
                      clearOutputState();
                    }}
                    type="number"
                    value={customLoopCount}
                  />
                </label>
              ) : (
                <div />
              )}
            </div>
          ) : null}

          <div className="tool-live-panel-note">
            <strong>Canvas behavior</strong>
            <span>
              Frames are rendered onto the largest uploaded canvas size so mixed-dimension PNGs can
              still turn into one APNG cleanly.
            </span>
          </div>

          {frames.length ? (
            <div className="tool-apng-frame-list">
              {frames.map((frame, index) => (
                <article className="tool-apng-frame-card" key={frame.id}>
                  <div className="tool-apng-frame-thumb">
                    <img alt="" src={frame.previewUrl} />
                  </div>
                  <div className="tool-apng-frame-copy">
                    <strong>{`Frame ${index + 1}`}</strong>
                    <span>{frame.name}</span>
                    <small>{`${frame.width} × ${frame.height} • ${formatFileSize(frame.size)}`}</small>
                  </div>
                  <div className="tool-apng-frame-controls">
                    <label className="field">
                      <span>Delay (ms)</span>
                      <input
                        min="20"
                        onChange={(event) =>
                          updateFrameDelay(
                            frame.id,
                            clamp(Number(event.target.value) || 20, 20, 5000),
                          )
                        }
                        type="number"
                        value={frame.delayMs}
                      />
                    </label>
                    <div className="tool-pdf-page-actions">
                      <button
                        className="tool-pdf-page-action"
                        disabled={index === 0}
                        onClick={() => moveFrame(frame.id, -1)}
                        type="button"
                      >
                        Up
                      </button>
                      <button
                        className="tool-pdf-page-action"
                        disabled={index === frames.length - 1}
                        onClick={() => moveFrame(frame.id, 1)}
                        type="button"
                      >
                        Down
                      </button>
                      <button
                        className="tool-pdf-page-action tool-pdf-page-action-danger"
                        onClick={() => removeFrame(frame.id)}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={frames.length < 2 || isProcessing}
              onClick={() => void runGeneration()}
              type="button"
            >
              {isProcessing ? 'Building APNG…' : 'Generate animated PNG'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download APNG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Animated PNG build</h3>
          <div className="tool-mockup-chip-list">
            <span>Multi-frame</span>
            <span>APNG export</span>
            <span>Browser-only</span>
          </div>
          <p>
            This keeps a PNG-based animation workflow intact when you need transparency, ordered
            frames, and predictable export timing without converting the sequence to GIF or video.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best fit</strong>
            <span>
              Great for transparent UI motion, lightweight stickers, product spins, and short loop
              sequences assembled from separate PNG files.
            </span>
          </div>
          {frames.length ? (
            <div className="tool-live-stats">
              <div>
                <strong>Frames</strong>
                <span>{frames.length}</span>
              </div>
              {canvasSize ? (
                <div>
                  <strong>Canvas</strong>
                  <span>{`${canvasSize.width} × ${canvasSize.height}`}</span>
                </div>
              ) : null}
              <div>
                <strong>Source total</strong>
                <span>{formatFileSize(sourceBytes)}</span>
              </div>
              <div>
                <strong>Source duration</strong>
                <span>{`${(sourceDuration / 1000).toFixed(sourceDuration >= 10_000 ? 1 : 2)} sec`}</span>
              </div>
              {resultDuration ? (
                <div>
                  <strong>Export duration</strong>
                  <span>{`${(resultDuration / 1000).toFixed(resultDuration >= 10_000 ? 1 : 2)} sec`}</span>
                </div>
              ) : null}
              {resultSize ? (
                <div>
                  <strong>APNG size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {frames.length ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Animated preview</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <img alt="First PNG frame preview" src={frames[0].previewUrl} />
            </div>
            <div className="tool-live-preview-frame">
              {generatedPreviewUrl ? (
                <img alt="Generated animated PNG preview" src={generatedPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>APNG preview</strong>
                  <span>Generate the animation to preview the loop.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}
